import {
	CUSTOM_FIELD_KEYS,
	ExtensionStayEnum,
	ExtensionStayLabel,
	PageTypeEnum,
	ResidenceEnum,
	ResidenceLabel,
	VisaTimeEnum,
	VisaTimeLabel,
} from '@/constant';
import {
	GetWorkerAssignmentFranchiseListResponse,
	GetWorkerAssignmentPublicApplicantListResponse,
	GetWorkerAssignmentPublicDetailResponse,
	IFranchiseListItem,
} from '@/services';

import {
	convertGenderCodeToName,
	filterByPrefixKeyWithFormat,
	formatCustomFieldValueToExcel,
	formatDate,
	formatNumberKR,
	getDiffExpirationDateString,
	insertObjectIfV2,
	map,
	showDataGridWithSymbol,
} from 'utils';

import { isNotEmpty } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import { formatWorkerFranchiseListItem } from '../worker-franchise';

export const formatWorkerAssignmentPublicDetail = (
	input: GetWorkerAssignmentPublicDetailResponse
) => {
	return {
		...input,
		registDt: formatDate(input.registDt),
		updateDt: formatDate(input.updateDt),
		startTime: formatDate(input.startTime, {
			inputFormat: 'HH:mm',
			outputFormat: 'HH:mm',
		}),
		endTime: formatDate(input.endTime, {
			inputFormat: 'HH:mm',
			outputFormat: 'HH:mm',
		}),
		relaxTimeString: `${formatNumberKR(input?.relaxTime)}시간`,
		assignmentPerRequestApplicant: `${formatNumberKR(input?.assignmentApplicant)}/${formatNumberKR(input?.requestApplicant)} 명`,
	};
};

export const formatWorkerAssignmentPublicListItem = (
	input: GetWorkerAssignmentFranchiseListResponse['content'][number]
) => {
	let _input = {
		...input,
	};

	let newFranchiseFormat = filterByPrefixKeyWithFormat<IFranchiseListItem>(
		_input,
		PageTypeEnum.FRANCHISE
	);
	if (isNotEmpty(newFranchiseFormat)) {
		newFranchiseFormat = formatWorkerFranchiseListItem(newFranchiseFormat);
	}

	const formatValues = {
		...newFranchiseFormat,
	} as typeof _input;

	return {
		..._input,
		...insertObjectIfV2(isNotEmpty(formatValues), {
			...formatValues,
		}),
		id: _input?.assignmentFranchiseId,
		assignmentFranchiseStartDate: formatDate(
			_input?.assignmentFranchiseStartDate
		),
		assignmentFranchiseEndDate: formatDate(_input?.assignmentFranchiseEndDate),
		assignmentFranchiseRegistDt: formatDate(
			_input?.assignmentFranchiseRegistDt
		),
		assignmentFranchiseUpdateDt: formatDate(
			_input?.assignmentFranchiseUpdateDt
		),
		assignmentFranchiseAssignmentApplicantPerRequestApplicant: `${showDataGridWithSymbol(
			input?.assignmentFranchiseAssignmentApplicant,
			input?.assignmentFranchiseRequestApplicant
		)?.replaceAll(' ', '')} 명`,
	};
};

export const formatWorkerAssignmentPublicList = (
	input: GetWorkerAssignmentFranchiseListResponse['content']
) => {
	return map(input, formatWorkerAssignmentPublicListItem);
};

export const formatWorkerAssignmentPublicExcelList = (
	input: GetWorkerAssignmentFranchiseListResponse['content']
) => {
	return map(input, (item) => {
		return {
			...formatWorkerAssignmentPublicListItem(item),
			franchiseDistrictId: item?.franchiseDistrictName,
			franchisePublicFranchiseId: item.franchisePublicFranchiseName,
			franchiseProductionScale: isNotNilOrEmpty(item?.franchiseProductionScale)
				? Number(item?.franchiseProductionScale)?.toLocaleString?.('ko-KR')
				: '',
			workingDateGroup: item?.assignmentFranchiseId,

			...formatCustomFieldValueToExcel(
				[...CUSTOM_FIELD_KEYS.franchise].reduce(
					(acc, key) => ({
						...acc,
						[key]: item?.[key as keyof typeof item],
					}),
					{}
				)
			),
		};
	});
};

export const formatWorkerAssignmentPublicApplicantDetail = (
	input: GetWorkerAssignmentPublicApplicantListResponse['content'][number]
) => {
	return {
		...input,
		birthDate: formatDate(input?.birthDate),
		workStartDate: formatDate(input?.workStartDate),
		workEndDate: formatDate(input?.workEndDate),
		entryDate: formatDate(input?.entryDate),
		extensionStayDate: formatDate(input?.extensionStayDate),
		expirationDate: isNotNilOrEmpty(input?.expirationDate)
			? getDiffExpirationDateString(input?.expirationDate)
			: '',
		changeWorkDate: formatDate(input?.changeWorkDate),
		unauthorizedDepartureDate: formatDate(input?.unauthorizedDepartureDate),
		departureDate: isNotNilOrEmpty(input?.departureDate)
			? getDiffExpirationDateString(input?.departureDate)
			: '',
		registDt: formatDate(input.registDt),
		visaCode: ResidenceLabel[input.visaCode] as ResidenceEnum,
		visaTime: VisaTimeLabel[input.visaTime] as VisaTimeEnum,
		heightPerWeight: showDataGridWithSymbol(input.height, input.weight),
		gender: convertGenderCodeToName(input?.gender || ''),
		salary: `${formatNumberKR(input.salary)}원`,
		grade: input?.grade
			? input?.grade === 'BASIC_GRADE'
				? '기본 등급'
				: `${input?.grade?.replace('GRADE_', '')}`
			: '',
		extensionPeriod: ExtensionStayLabel[
			input.extensionPeriod
		] as ExtensionStayEnum,
	};
};

export const formatWorkerAssignmentPublicApplicantList = (
	input: GetWorkerAssignmentPublicApplicantListResponse['content']
) => {
	return map(input, formatWorkerAssignmentPublicApplicantDetail);
};
